import { useState } from 'react';
import { Dialog } from '@headlessui/react';
import { supabase } from '../../services/supabase';
import { ConfirmationModal } from '../shared/ConfirmationModal';
import { useData } from '../../contexts/DataContext';
import { Edit, Delete } from '@mui/icons-material';
import { useNotification } from '../../contexts/NotificationContext';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';
import { ColDef, DomLayoutType, ICellRendererParams } from 'ag-grid-community';
import DataTable from '../shared/DataTable';

interface Brand {
  id: string;
  name: string;
  description: string | null;
  domain_id: string;
}

interface BrandFormData {
  name: string;
  description: string | null;
  domain_id: string;
}

const BrandsTab = () => {
  const { t } = useTranslation();
  const { domainId } = useParams<{ domainId: string }>();
  const { brands, domains, fetchRelatedDomainData } = useData();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState<Brand | null>(null);
  const [formData, setFormData] = useState<BrandFormData>({
    name: '',
    description: null,
    domain_id: domainId || '',
  });
  const { showNotification } = useNotification();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Verify domain exists
    const domainExists = domains.some((d) => d.id === formData.domain_id);
    if (!domainExists) {
      showNotification(t('brands.domain_not_found'), 'error');
      return;
    }

    const operation = selectedBrand
      ? supabase.from('brands').update(formData).eq('id', selectedBrand.id)
      : supabase.from('brands').insert([formData]);

    const { error } = await operation;

    if (error) {
      console.error('Error saving brand:', error);
      showNotification(t('brands.error_saving_brand'), 'error');
      Sentry.captureException(error);
    } else {
      setIsModalOpen(false);
      fetchRelatedDomainData(domainId);
      resetForm();
      showNotification(t('brands.brand_saved_successfully'), 'success');
    }
  };

  const handleDelete = async () => {
    if (!selectedBrand) return;

    const { error } = await supabase.from('brands').delete().eq('id', selectedBrand.id);

    if (error) {
      console.error('Error deleting brand:', error);
      showNotification(t('brands.error_deleting_brand'), 'error');
      Sentry.captureException(error);
    } else {
      setIsDeleteModalOpen(false);
      fetchRelatedDomainData(domainId);
      setSelectedBrand(null);
      showNotification(t('brands.brand_deleted_successfully'), 'success');
    }
  };

  const resetForm = () => {
    setFormData({ name: '', description: null, domain_id: domainId || '' });
    setSelectedBrand(null);
  };

  const columnDefs: ColDef<Brand>[] = [
    {
      field: 'name',
      headerName: t('brands.name'),
      sortable: true,
      filter: true,
      onCellClicked: (params) => {
        setSelectedBrand(params.data as Brand);
        setFormData(params.data as Brand);
        setIsModalOpen(true);
      },
      cellStyle: {
        cursor: 'pointer',
        color: '#3b82f6', // blue-500 color in Tailwind
      },
    },
    {
      field: 'description',
      headerName: t('brands.description'),
      sortable: true,
      filter: true,
      tooltipField: 'description',
    },
    {
      field: 'domain_id',
      headerName: t('brands.domain'),
      sortable: true,
      filter: true,
      valueFormatter: (params) => {
        return domains.find((d) => d.id === params.value)?.domain || '';
      },
    },
    {
      headerName: t('brands.actions'),
      sortable: false,
      filter: false,
      cellRenderer: (params: ICellRendererParams<Brand>) => {
        return (
          <div className="flex space-x-2">
            <button
              onClick={() => {
                setSelectedBrand(params.data as Brand);
                setFormData(params.data as Brand);
                setIsModalOpen(true);
              }}
              className="text-blue-500 hover:text-blue-700 mr-2"
            >
              <Edit className="h-5 w-5" />
            </button>
            <button
              onClick={() => {
                setSelectedBrand(params.data as Brand);
                setIsDeleteModalOpen(true);
              }}
              className="text-red-500 hover:text-red-700"
            >
              <Delete className="h-5 w-5" />
            </button>
          </div>
        );
      },
    },
  ];

  const gridConfig = {
    pagination: true,
    paginationAutoPageSize: true,
    defaultColDef: {
      flex: 1,
      minWidth: 100,
      resizable: true,
    },
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-xl font-semibold">{t('brands.brand_management')}</h3>
        <button
          onClick={() => {
            resetForm();
            setIsModalOpen(true);
          }}
          className="px-2 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          {t('brands.add_brand')}
        </button>
      </div>

      <div className="overflow-x-auto bg-white rounded-sm shadow flex-grow min-h-0">
        <DataTable<Brand>
          rowData={brands}
          columnDefs={columnDefs}
          gridConfig={gridConfig}
          isLoading={false}
          theme="ag-theme-alpine"
          height={'100%'}
          noRowsMessage={t('brands.no_brands')}
        />
      </div>

      <Dialog open={isModalOpen} onClose={() => setIsModalOpen(false)} className="relative z-50">
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="bg-white rounded-lg p-6 max-w-md w-full">
            <Dialog.Title className="text-lg font-medium mb-4">
              {selectedBrand ? t('brands.edit_brand') : t('brands.add_brand')}
            </Dialog.Title>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block mb-1">{t('brands.name')}</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                  required
                />
              </div>
              <div>
                <label className="block mb-1">{t('brands.description')}</label>
                <textarea
                  rows={6}
                  value={formData.description || ''}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value as string | null })
                  }
                  className="w-full border rounded px-3 py-2"
                />
              </div>
              <div>
                <label className="block mb-1">{t('brands.domain')}</label>
                <select
                  value={formData.domain_id}
                  onChange={(e) => setFormData({ ...formData, domain_id: e.target.value })}
                  className="w-full border rounded px-3 py-2 bg-gray-100"
                  required
                  disabled
                >
                  <option value="">{t('brands.select_domain')}</option>
                  {domains.map((domain) => (
                    <option key={domain.id} value={domain.id}>
                      {domain.domain}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex justify-end space-x-4">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-2 py-1 text-sm bg-gray-200 rounded hover:bg-gray-300"
                >
                  {t('brands.cancel')}
                </button>
                <button
                  type="submit"
                  className="px-2 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600"
                >
                  {selectedBrand ? t('brands.update') : t('brands.create')}
                </button>
              </div>
            </form>
          </Dialog.Panel>
        </div>
      </Dialog>

      <ConfirmationModal
        open={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDelete}
        title={t('brands.delete_brand')}
        message={t('brands.confirm_delete')}
      />
    </div>
  );
};

export default BrandsTab;
